import { zip } from "fflate";

import {
  DEFAULT_EFFECT,
  type EditorImage,
  type ExportFormat,
} from "../editor/types";
import { preserveMetadata } from "./metadata";

const IMAGE_TYPES = new Map([
  [".png", "image/png"],
  [".jpg", "image/jpeg"],
  [".jpeg", "image/jpeg"],
  [".webp", "image/webp"],
  [".bmp", "image/bmp"],
]);

const MAX_DIMENSION = 8192;

function extensionOf(name: string): string {
  const position = name.lastIndexOf(".");
  return position >= 0 ? name.slice(position).toLowerCase() : "";
}

function sanitizeFileName(name: string): string {
  const basename = name.split(/[\\/]/).pop() ?? name;
  const cleaned = basename.replace(
    /[^\w.\-\u3000-\u9fff\uff00-\uffef ]/g,
    "_",
  );
  return cleaned || "image";
}

export function isSupportedImage(file: File): boolean {
  return IMAGE_TYPES.has(extensionOf(file.name));
}

const EXPORT_EXTENSIONS: Record<Exclude<ExportFormat, "original">, string> = {
  png: ".png",
  jpeg: ".jpg",
  webp: ".webp",
};

export function outputName(
  name: string,
  format: ExportFormat = "original",
): string {
  const position = name.lastIndexOf(".");
  const originalExtension = position >= 0 ? name.slice(position) : "";
  const extension =
    format === "original" ? originalExtension : EXPORT_EXTENSIONS[format];
  const base = originalExtension ? name.slice(0, position) : name;
  return `${base}_mc${extension || ".png"}`;
}

function canvasToBlob(
  canvas: HTMLCanvasElement,
  mime: string,
  quality?: number,
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) =>
        blob
          ? resolve(blob)
          : reject(new Error("画像を書き出せませんでした")),
      mime,
      quality,
    );
  });
}

function createThumbnail(
  source: CanvasImageSource,
  width: number,
  height: number,
): Promise<Blob> {
  const maxWidth = 240;
  const scale = Math.min(1, maxWidth / width);
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(width * scale));
  canvas.height = Math.max(1, Math.round(height * scale));
  const context = canvas.getContext("2d");
  if (!context) throw new Error("サムネイルを作成できませんでした");
  context.drawImage(source, 0, 0, canvas.width, canvas.height);
  return canvasToBlob(canvas, "image/jpeg", 0.82);
}

export async function fileToEditorImage(
  file: File,
  relativePath?: string,
): Promise<EditorImage> {
  if (!isSupportedImage(file)) {
    throw new Error(`対応していない画像形式です: ${file.name}`);
  }
  const bitmap = await createImageBitmap(file, {
    imageOrientation: "from-image",
  });
  try {
    if (bitmap.width > MAX_DIMENSION || bitmap.height > MAX_DIMENSION) {
      throw new Error(
        `画像が大きすぎます (最大 ${MAX_DIMENSION}×${MAX_DIMENSION}px): ${bitmap.width}×${bitmap.height}`,
      );
    }
    if (bitmap.width === 0 || bitmap.height === 0) {
      throw new Error("画像の幅または高さが0です");
    }
    const normalized = document.createElement("canvas");
    normalized.width = bitmap.width;
    normalized.height = bitmap.height;
    const context = normalized.getContext("2d", {
      colorSpace: "srgb",
    });
    if (!context) throw new Error("画像Canvasを初期化できませんでした");
    context.drawImage(bitmap, 0, 0);
    const normalizedMime =
      file.type && file.type !== "image/bmp" ? file.type : "image/png";
    const normalizedBlob = await canvasToBlob(
      normalized,
      normalizedMime,
      normalizedMime === "image/jpeg" ? 0.95 : undefined,
    );
    const thumbnailBlob = await createThumbnail(
      normalized,
      normalized.width,
      normalized.height,
    );
    const safeName = sanitizeFileName(file.name);
    return {
      id: crypto.randomUUID(),
      name: safeName,
      mime: IMAGE_TYPES.get(extensionOf(safeName)) ?? "image/png",
      width: normalized.width,
      height: normalized.height,
      sourceUrl: URL.createObjectURL(normalizedBlob),
      thumbnailUrl: URL.createObjectURL(thumbnailBlob),
      originalBytes: await file.arrayBuffer(),
      coverage: "inside",
      coverageEffect: DEFAULT_EFFECT,
      regions: [],
      restorations: [],
      dirty: false,
      saved: false,
      relativePath,
    };
  } finally {
    bitmap.close();
  }
}

export async function loadDemoImage(): Promise<EditorImage> {
  const response = await fetch("/demo/park-portrait.png");
  if (!response.ok) throw new Error("デモ画像を読み込めませんでした");
  const blob = await response.blob();
  return fileToEditorImage(
    new File([blob], "sample-photo.png", { type: "image/png" }),
  );
}

export async function imageElement(url: string): Promise<HTMLImageElement> {
  const image = new Image();
  image.decoding = "async";
  image.src = url;
  await image.decode();
  return image;
}

export async function composeSourceImage(
  image: EditorImage,
): Promise<ImageData> {
  const canvas = document.createElement("canvas");
  canvas.width = image.width;
  canvas.height = image.height;
  const context = canvas.getContext("2d", {
    willReadFrequently: true,
    colorSpace: "srgb",
  });
  if (!context) throw new Error("画像Canvasを初期化できませんでした");
  const source = await imageElement(image.sourceUrl);
  context.drawImage(source, 0, 0, image.width, image.height);
  for (const patch of image.restorations) {
    const restored = await imageElement(patch.dataUrl);
    context.globalAlpha = patch.strength / 100;
    context.drawImage(
      restored,
      patch.x,
      patch.y,
      patch.width,
      patch.height,
    );
  }
  context.globalAlpha = 1;
  return context.getImageData(0, 0, image.width, image.height);
}

function encodeBmp(imageData: ImageData): Blob {
  const { width, height, data } = imageData;
  const rowSize = Math.ceil((width * 3) / 4) * 4;
  const pixelSize = rowSize * height;
  const bytes = new Uint8Array(54 + pixelSize);
  const view = new DataView(bytes.buffer);
  bytes[0] = 0x42;
  bytes[1] = 0x4d;
  view.setUint32(2, bytes.length, true);
  view.setUint32(10, 54, true);
  view.setUint32(14, 40, true);
  view.setInt32(18, width, true);
  view.setInt32(22, height, true);
  view.setUint16(26, 1, true);
  view.setUint16(28, 24, true);
  view.setUint32(34, pixelSize, true);
  for (let y = 0; y < height; y += 1) {
    const destinationRow = height - 1 - y;
    for (let x = 0; x < width; x += 1) {
      const sourceOffset = (y * width + x) * 4;
      const destinationOffset = 54 + destinationRow * rowSize + x * 3;
      const sourceAlpha = data[sourceOffset + 3] / 255;
      bytes[destinationOffset] = Math.round(
        data[sourceOffset + 2] * sourceAlpha + 255 * (1 - sourceAlpha),
      );
      bytes[destinationOffset + 1] = Math.round(
        data[sourceOffset + 1] * sourceAlpha + 255 * (1 - sourceAlpha),
      );
      bytes[destinationOffset + 2] = Math.round(
        data[sourceOffset] * sourceAlpha + 255 * (1 - sourceAlpha),
      );
    }
  }
  return new Blob([bytes], { type: "image/bmp" });
}

export interface ExportOptions {
  /**
   * Drops EXIF/XMP/ICC from the output. Orientation is already normalised at
   * import time (see `normalizeExifOrientation`), so stripping never rotates
   * the exported image.
   */
  stripMetadata: boolean;
  format: ExportFormat;
  scale: number;
  /** JPEG/WebP quality, 60-100. Ignored for PNG and BMP. */
  quality: number;
}

function exportMime(image: EditorImage, format: ExportFormat): string {
  if (format !== "original") return `image/${format}`;
  return ["image/png", "image/jpeg", "image/webp", "image/bmp"].includes(
    image.mime,
  )
    ? image.mime
    : "image/png";
}

export function exportDimensions(
  width: number,
  height: number,
  scale: number,
): { width: number; height: number } {
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

export async function imageDataToExportBlob(
  imageData: ImageData,
  image: EditorImage,
  options: ExportOptions,
): Promise<Blob> {
  const mime = exportMime(image, options.format);
  const size = exportDimensions(imageData.width, imageData.height, options.scale);
  const canvas = document.createElement("canvas");
  canvas.width = size.width;
  canvas.height = size.height;
  const context = canvas.getContext("2d");
  if (!context) throw new Error("書き出しCanvasを初期化できませんでした");
  const sourceCanvas = document.createElement("canvas");
  sourceCanvas.width = imageData.width;
  sourceCanvas.height = imageData.height;
  const sourceContext = sourceCanvas.getContext("2d");
  if (!sourceContext) throw new Error("書き出し元Canvasを初期化できませんでした");
  sourceContext.putImageData(imageData, 0, 0);
  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = "high";
  context.drawImage(sourceCanvas, 0, 0, size.width, size.height);
  const resized = context.getImageData(0, 0, size.width, size.height);
  if (mime === "image/bmp") return encodeBmp(resized);
  const lossy = mime === "image/jpeg" || mime === "image/webp";
  const blob = await canvasToBlob(
    canvas,
    mime,
    lossy ? Math.max(0.6, Math.min(1, options.quality / 100)) : undefined,
  );
  if (options.stripMetadata) return blob;
  return preserveMetadata(image.originalBytes, blob, mime);
}

export function downloadBlob(blob: Blob, name: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = name;
  link.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 1_000);
}

export async function zipBlobs(
  entries: Array<{ name: string; blob: Blob }>,
): Promise<Blob> {
  const files: Record<string, Uint8Array> = {};
  for (const entry of entries) {
    files[entry.name] = new Uint8Array(await entry.blob.arrayBuffer());
  }
  const archive = await new Promise<Uint8Array>((resolve, reject) => {
    zip(files, { level: 6 }, (error, data) => {
      if (error) {
        reject(error);
      } else {
        resolve(data);
      }
    });
  });
  return new Blob([archive.slice().buffer as ArrayBuffer], {
    type: "application/zip",
  });
}

export function releaseEditorImage(image: EditorImage): void {
  URL.revokeObjectURL(image.sourceUrl);
  URL.revokeObjectURL(image.thumbnailUrl);
  image.restorations.forEach((patch) => {
    if (patch.dataUrl.startsWith("blob:")) URL.revokeObjectURL(patch.dataUrl);
  });
}
